import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { PageCard } from './PageCard';
import { CarouselDots } from './CarouselDots';
import type { VolumePage } from '../data/volumeOnePages';
type Props = {
  pages: VolumePage[];
  onOpen: (index: number) => void;
};
const ease = [0.23, 1, 0.32, 1] as const;

/** Three cards at once, center card larger, wraps around. md and up. */
export function LookInsideDesktopCarousel({
  pages,
  onOpen
}: Props) {
  const [active, setActive] = useState(0);
  const reduceMotion = useReducedMotion();
  const n = pages.length;
  const go = (dir: 1 | -1) => setActive(a => (a + dir + n) % n);
  return <div className="hidden md:block" data-id="element-149" data-mp="14hejohjumose">
      <div className="relative" role="region" aria-roledescription="carousel" aria-label="Volume 1 pages" onKeyDown={e => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    }} data-id="element-150" data-mp="14gcyhk94clwy">
        {/* Sizer: reserves the height of the center card + caption */}
        <div aria-hidden="true" className="invisible mx-auto w-[30%] max-w-[330px]" data-id="element-151" data-mp="2fzdgvxet3mwf">
          <div className="aspect-[9/16]" data-id="element-152" data-mp="2unqc2s62l1pv" />
          <div className="mt-4 min-h-[3rem]" data-id="element-153" data-mp="2uojclbefquzk" />
        </div>

        {pages.map((page, i) => {
        let offset = (i - active + n) % n;
        if (offset > n / 2) offset -= n;
        const isCenter = offset === 0;
        const isVisible = Math.abs(offset) <= 1;
        return <motion.figure key={page.src} aria-hidden={!isVisible} initial={false} animate={{
          x: `${offset * 108}%`,
          scale: isCenter ? 1 : 0.84,
          opacity: isCenter ? 1 : isVisible ? 0.75 : 0
        }} transition={{
          duration: reduceMotion ? 0 : 0.3,
          ease
        }} style={{
          zIndex: isCenter ? 3 : isVisible ? 2 : 1
        }} className={`absolute inset-x-0 top-0 mx-auto w-[30%] max-w-[330px] ${isVisible ? '' : 'pointer-events-none'}`} data-id="element-154" data-mp="2zls0lsm9lt8q" data-mpi={i}>
              <PageCard page={page} onOpen={() => onOpen(i)} tabbable={isVisible} data-id="element-155" data-mp="3jc5p3h5vqniy" />
              <figcaption className="mt-4 min-h-[3rem] text-center font-sans text-[15px] leading-snug text-[#22303C]" data-id="element-156" data-mp="3sb970htb8wvv">
                {page.caption}
              </figcaption>
            </motion.figure>;
      })}

        <button type="button" onClick={() => go(-1)} aria-label="Previous page" className="absolute left-0 top-[calc(50%-1.5rem)] z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#E6E2DA] bg-white text-[#22303C] shadow-card transition-[background-color,transform] duration-150 ease-out hover:bg-[#FAF7F2] active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4C5FD5]/40" data-id="element-157" data-mp="627ywbawepx4">
          <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-158" data-mp="yz0fxo3ap29h" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next page" className="absolute right-0 top-[calc(50%-1.5rem)] z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#E6E2DA] bg-white text-[#22303C] shadow-card transition-[background-color,transform] duration-150 ease-out hover:bg-[#FAF7F2] active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#4C5FD5]/40" data-id="element-159" data-mp="tyurshw8hxw5">
          <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-160" data-mp="17ydomejlhmt5" />
        </button>
      </div>

      <CarouselDots count={n} active={active} onSelect={setActive} labels={pages.map(p => p.caption)} data-id="element-161" data-mp="13xtt1feo6r94" />
    </div>;
}